// Model + helpers for the artwork template body the matchcard engine consumes
// (apps/api/shared/matchcard_generator/matchcard_engine.py), schema
// tc.artwork.template.v3. Every field here is one the engine actually reads, so
// the round-trip preview can never render something the editor can't express.
//
// Body: { name, canvas, targeting, layers[] }.
//
// Two things about the schema shape the whole editor:
//
//  1. It is STRICT (additionalProperties: false). Publish rejects any key it
//     doesn't know, so unlike the old v1 model there is no "keep unknown keys
//     around" escape hatch — a stray key is a publish failure, not a no-op.
//     Layers therefore have no index signature, and editor-only state is kept
//     outside the body.
//  2. Layers are a TREE. A group layer holds nested `layers`, and a group has
//     no id of its own (the schema forbids one), so layers are addressed by
//     path rather than by id. See LayerPath.

// ---------------------------------------------------------------------------
// Layer types
// ---------------------------------------------------------------------------

export type LayerType = 'image' | 'text' | 'shape' | 'group'

// The 9 anchor points, in 3×3 grid reading order (row-major). The engine also
// accepts 'left'/'right' as aliases of 'center-left'/'center-right'; we
// normalize those on read so the grid has one cell per position.
export type AnchorPoint =
  | 'top-left'
  | 'top'
  | 'top-right'
  | 'center-left'
  | 'center'
  | 'center-right'
  | 'bottom-left'
  | 'bottom'
  | 'bottom-right'

export const ANCHOR_POINTS: AnchorPoint[] = [
  'top-left',
  'top',
  'top-right',
  'center-left',
  'center',
  'center-right',
  'bottom-left',
  'bottom',
  'bottom-right',
]

const ANCHOR_ALIASES: Record<string, AnchorPoint> = {
  left: 'center-left',
  right: 'center-right',
}

export const normalizeAnchor = (raw: unknown): AnchorPoint => {
  const s = typeof raw === 'string' ? raw : ''
  if (ANCHOR_ALIASES[s]) return ANCHOR_ALIASES[s]
  return (ANCHOR_POINTS as string[]).includes(s) ? (s as AnchorPoint) : 'top-left'
}

export type Team = 'home' | 'away'

/** Fields a text layer can bind to on a player. */
export type BoundField = 'first_name' | 'last_name' | 'full_name' | 'country_code'

/** Per-player assets an image layer can bind to. */
export type EntityAsset = 'headshot' | 'flag' | 'banner'

/** Assets of the match's own entities (tournament, level, match). */
export type MatchAsset = 'logo' | 'banner' | 'card'

export type TextTransform = 'uppercase' | 'lowercase' | 'titlecase'

export type ImageFit = 'cover' | 'width' | 'stretch'

export type MatchType = 'singles' | 'doubles' | 'mixed_doubles'

// ---------------------------------------------------------------------------
// Body shapes (mirroring tc.artwork.template.v2 exactly)
// ---------------------------------------------------------------------------

export type Point = [number, number]
export type NullableSize = [number | null, number | null]

export interface AnchorTo {
  layer: string
  anchor: AnchorPoint
  self?: AnchorPoint
  offset?: Point
}

/** An image reference: the pinned id plus the s3_key kept as a human label. */
export interface ImageRef {
  tc_image_id: string | null
  label?: string
}

/**
 * One condition, shared by a layer's `show_when` and a rule's `when`.
 *
 * A leaf compares one bound path; not/all/any compose leaves. The composition
 * is what lets a rule say "both sides have headshots" without a group nested
 * inside a group — the shape the doubles plate used to need.
 */
export type Condition =
  | { path: string; equals?: string | boolean; in?: (string | boolean)[] }
  | { not: Condition }
  | { all: Condition[] }
  | { any: Condition[] }

export const isNot = (c: Condition): c is { not: Condition } => 'not' in c
export const isAll = (c: Condition): c is { all: Condition[] } => 'all' in c
export const isAny = (c: Condition): c is { any: Condition[] } => 'any' in c
export const isLeaf = (
  c: Condition
): c is { path: string; equals?: string | boolean; in?: (string | boolean)[] } =>
  'path' in c

/**
 * One entry of `sources`.
 *
 * A rule is used when its condition holds AND its image loads. The second
 * gate is the fallback mechanism: a headshot rule and a flag rule are both
 * unconditional, and the flag is reached only because the headshot did not
 * resolve. The editor must never present the list as pure condition
 * dispatch, or a reader concludes rule 2 is unreachable.
 */
export interface SourceRule {
  /** Absent means "always" — and on the last rule, that is the backstop. */
  when?: Condition
  /** A pinned image. Exclusive with entity/asset and with reserve. */
  tc_image_id?: string | null
  label?: string
  /** A player's art ("<team>.players[<n>]") or the match's ("tournament"). */
  entity?: string
  asset?: EntityAsset | MatchAsset
  /** Draws nothing, holds a box open for the anchor chain. */
  reserve?: { size: Point }
}

export interface ImageBox {
  at?: Point
  size?: NullableSize
  /** 'canvas' stretches the asset over the whole frame (the background case). */
  fill?: 'canvas'
  fit?: ImageFit
}

export interface ImageLayer {
  /** Internal key: unique, generated, never shown or renamed. */
  id: string
  /** What authors see. Free text; duplicates are fine. */
  name?: string
  type: 'image'
  z?: number
  box?: ImageBox
  /** Ordered. The engine draws the first rule that fits. */
  sources: SourceRule[]
  show_when?: Condition
  anchor_to?: AnchorTo
  at?: Point
}

export type TextContent = { static: string } | { bound: string }

/**
 * One entry of a text layer's `content`, the text counterpart of SourceRule.
 *
 * The second gate is spelled differently here. A bound rule is skipped when
 * its path resolves to nothing — that is what lets a last name fall back to a
 * full name — but a static rule always has its text and so ends the list, an
 * empty one included. "Blank for this case" is therefore writable, and the
 * editor must not present it as a rule that falls through.
 */
export type TextRule = { when?: Condition } & TextContent

/** A rule saying `content`, keeping the condition that reached it. */
export function textRuleWith(previous: TextRule, content: TextContent): TextRule {
  return previous.when ? { when: previous.when, ...content } : { ...content }
}

/** What one text rule draws, for the tree and the rule header. */
export const describeTextRule = (rule: TextRule): string =>
  'static' in rule ? `“${rule.static}”` : rule.bound

export interface TextLayer {
  /** Internal key: unique, generated, never shown or renamed. */
  id: string
  /** What authors see. Free text; duplicates are fine. */
  name?: string
  type: 'text'
  z?: number
  /** Ordered. The engine draws the first rule that fits. */
  content: TextRule[]
  font: { tc_font_id: string | null; label?: string; size: number }
  max_width?: number
  min_size?: number
  transform?: TextTransform
  color?: string
  show_when?: Condition
  anchor_to?: AnchorTo
  at?: Point
}

export interface ShapeLayer {
  /** Internal key: unique, generated, never shown or renamed. */
  id: string
  /** What authors see. Free text; duplicates are fine. */
  name?: string
  type: 'shape'
  z?: number
  size: Point
  color?: string
  opacity?: number
  skew_x?: number
  show_when?: Condition
  anchor_to?: AnchorTo
  at?: Point
}

/**
 * A group. Note: no `id` — the schema forbids one. show_when hides the whole
 * group, as on any layer; an either/or is two groups with opposite conditions.
 */
export interface GroupLayer {
  type: 'group'
  /** Optional; the tree shows the condition when there is none. */
  name?: string
  show_when?: Condition
  layers: Layer[]
}

export type Layer = ImageLayer | TextLayer | ShapeLayer | GroupLayer

export interface Targeting {
  categories?: string[]
  types: MatchType[]
  resolution: string
  default_for_unknown_category?: boolean
}

export interface TemplateBody {
  name: string
  canvas: { width: number; height: number }
  targeting: Targeting
  layers: Layer[]
}

// Narrowing helpers — `type` is the discriminant everywhere.
export const isGroup = (l: Layer): l is GroupLayer => l.type === 'group'
export const isImage = (l: Layer): l is ImageLayer => l.type === 'image'
export const isText = (l: Layer): l is TextLayer => l.type === 'text'
export const isShape = (l: Layer): l is ShapeLayer => l.type === 'shape'

/** Every layer type except group carries an id and can be positioned. */
export type PlacedLayer = ImageLayer | TextLayer | ShapeLayer
export const isPlaced = (l: Layer): l is PlacedLayer => l.type !== 'group'

// ---------------------------------------------------------------------------
// Layer paths
//
// Groups nest, and they have no id, so a layer is addressed by its position in
// the tree: array indices with 'layers' marking a descent into a group.
// [3, 'layers', 0] is layers[3].layers[0] — the engine's box keys match.
// ---------------------------------------------------------------------------

export type LayerPath = Array<number | 'layers'>

export const pathKey = (path: LayerPath): string => path.join('.')

export const samePath = (a: LayerPath | null, b: LayerPath | null): boolean =>
  a !== null && b !== null && pathKey(a) === pathKey(b)

/** The layer at `path`, or null if the path doesn't resolve. */
export function getLayerAt(layers: Layer[], path: LayerPath): Layer | null {
  let list: Layer[] = layers
  let layer: Layer | null = null
  for (const step of path) {
    if (typeof step === 'number') {
      layer = list[step] ?? null
      if (!layer) return null
    } else {
      if (!layer || !isGroup(layer)) return null
      list = layer.layers ?? []
      layer = null
    }
  }
  return layer
}

/**
 * Rebuild the tree with `fn` applied to the layer at `path`. Returning null
 * from `fn` deletes it. Structural sharing isn't attempted — bodies are small
 * and every edit already goes through a JSON round trip for history.
 */
export function mapLayerAt(
  layers: Layer[],
  path: LayerPath,
  fn: (layer: Layer) => Layer | null
): Layer[] {
  const walk = (list: Layer[], prefix: LayerPath): Layer[] => {
    const out: Layer[] = []
    list.forEach((layer, i) => {
      const here: LayerPath = [...prefix, i]
      if (pathKey(here) === pathKey(path)) {
        const next = fn(layer)
        if (next) out.push(next)
        return
      }
      if (isGroup(layer)) {
        out.push({ ...layer, layers: walk(layer.layers ?? [], [...here, 'layers']) })
        return
      }
      out.push(layer)
    })
    return out
  }
  return walk(layers, [])
}

/** Patch the (placed) layer at `path`. */
export function patchLayerAt(
  layers: Layer[],
  path: LayerPath,
  patch: Record<string, unknown>
): Layer[] {
  return mapLayerAt(layers, path, (layer) => ({ ...layer, ...patch }) as Layer)
}

export function removeLayerAt(layers: Layer[], path: LayerPath): Layer[] {
  return mapLayerAt(layers, path, () => null)
}

/** Append `layer` into a group (by its path), or the root when null. */
export function appendLayer(
  layers: Layer[],
  parent: LayerPath | null,
  layer: Layer
): { layers: Layer[]; path: LayerPath } {
  if (!parent) {
    return { layers: [...layers, layer], path: [layers.length] }
  }
  const group = getLayerAt(layers, parent)
  if (!group || !isGroup(group)) return { layers, path: parent }
  const inner = group.layers ?? []
  const next = mapLayerAt(layers, parent, (g) => ({
    ...(g as GroupLayer),
    layers: [...inner, layer],
  }))
  return { layers: next, path: [...parent, 'layers', inner.length] }
}

/** Every placed layer in the tree, with its path, in declaration order. */
export function flattenLayers(
  layers: Layer[],
  prefix: LayerPath = []
): { layer: Layer; path: LayerPath }[] {
  const out: { layer: Layer; path: LayerPath }[] = []
  layers.forEach((layer, i) => {
    const here: LayerPath = [...prefix, i]
    out.push({ layer, path: here })
    if (isGroup(layer)) {
      out.push(...flattenLayers(layer.layers ?? [], [...here, 'layers']))
    }
  })
  return out
}

/** `not` of a condition, collapsing a double negative (as body_upgrade does). */
export const negateCondition = (c: Condition): Condition => (isNot(c) ? c.not : { not: c })

const sameCondition = (a: Condition | undefined, b: Condition | undefined): boolean =>
  a !== undefined && b !== undefined && stableJson(a) === stableJson(b)

const stableJson = (value: unknown): string =>
  JSON.stringify(value, (_k, v) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(Object.entries(v).sort(([x], [y]) => x.localeCompare(y)))
      : v
  )

/** Two layers under sibling groups with opposite conditions never co-render. */
const mutuallyExclusive = (layers: Layer[], a: LayerPath, b: LayerPath): boolean => {
  const shared = Math.min(a.length, b.length)
  for (let i = 0; i < shared; i++) {
    if (a[i] === b[i]) continue
    const parent = a.slice(0, i)
    const list = parent.length ? (getLayerAt(layers, parent.slice(0, -1)) as GroupLayer | null)?.layers : layers
    const ga = list?.[a[i] as number]
    const gb = list?.[b[i] as number]
    return Boolean(
      ga && gb && isGroup(ga) && isGroup(gb) && ga.show_when &&
        sameCondition(gb.show_when, negateCondition(ga.show_when))
    )
  }
  return false
}

/**
 * Ids a layer at `ownPath` may reference from anchor_to.layer, plus the
 * 'canvas' pseudo-layer.
 *
 * Only layers the engine has already drawn qualify. render_with_boxes adds a
 * layer's box *after* drawing it, in ascending z, and resolve_position falls
 * back to the absolute position when the reference has no box yet. So an anchor
 * pointing at a higher-z layer doesn't error — it silently ignores anchor_to,
 * which leaves the layer stuck: the form shows Offset X/Y, dragging snaps back,
 * and typing an offset moves it nowhere.
 *
 * Ties matter: equal z keeps body order, the same stable sort the engine uses,
 * so a same-z layer declared earlier is legitimately available.
 *
 * Layers in a sibling group arm are excluded too — they never co-render, so
 * such a reference could never resolve.
 */
export function anchorTargets(layers: Layer[], ownPath: LayerPath): string[] {
  const placed = flattenLayers(layers).filter(
    (entry): entry is { layer: PlacedLayer; path: LayerPath } => isPlaced(entry.layer)
  )
  const drawOrder = byZ(placed)
  const rank = new Map(drawOrder.map((entry, i) => [pathKey(entry.path), i]))
  const ownRank = rank.get(pathKey(ownPath))
  if (ownRank === undefined) return ['canvas']

  const ids = drawOrder
    .filter(
      (entry) =>
        (rank.get(pathKey(entry.path)) ?? 0) < ownRank &&
        !mutuallyExclusive(layers, entry.path, ownPath)
    )
    .map((entry) => entry.layer.id)
    .filter(Boolean)
  return ['canvas', ...new Set(ids)]
}

// ---------------------------------------------------------------------------
// Bound / entity paths
// ---------------------------------------------------------------------------

/** "home.players[0]" — the only entity shape the schema allows. */
export const entityPath = (team: Team, index: number): string =>
  `${team}.players[${index}]`

export const boundPath = (team: Team, index: number, field: BoundField): string =>
  `${entityPath(team, index)}.${field}`

export interface ParsedEntity {
  team: Team
  index: number
}

export function parseEntity(path: string | undefined): ParsedEntity {
  const m = /^(home|away)\.players\[(\d+)\]/.exec(path ?? '')
  return m ? { team: m[1] as Team, index: Number(m[2]) } : { team: 'home', index: 0 }
}

export function parseBound(
  path: string | undefined
): ParsedEntity & { field: BoundField } {
  const m = /^(home|away)\.players\[(\d+)\]\.(first_name|last_name|full_name|country_code)$/.exec(
    path ?? ''
  )
  return m
    ? { team: m[1] as Team, index: Number(m[2]), field: m[3] as BoundField }
    : { team: 'home', index: 0, field: 'last_name' }
}

// ---------------------------------------------------------------------------
// Conditions
//
// Every value list here mirrors shared/artwork_paths.py, which the API's
// test_context_bindings.py holds this file to.
// ---------------------------------------------------------------------------

/** The hero-card lifecycle phases a hero condition may read.
 * Mirrors HERO_PHASE_* in shared/matchcard_generator/constants.py. */
export const HERO_PHASES = ['upcoming', 'live', 'ended', 'replay'] as const
export type HeroPhase = (typeof HERO_PHASES)[number]
export const TIERS = ['grand_slam', 'finals', '1000', '500', '250', '125', 'challenger', 'other'] as const
export const SURFACES = ['hard', 'clay', 'grass', 'carpet', 'other'] as const
export const ROUND_STAGES = ['final', 'semifinal', 'quarterfinal', 'earlier', 'qualifying'] as const
export const FORMATS = ['singles', 'doubles', 'mixed_doubles'] as const
export const GENDERS = ['men', 'women', 'mixed'] as const
export const ENTRIES = ['wildcard', 'qualifier', 'lucky_loser', 'alternate'] as const

/**
 * The subjects a condition can test, and the shape of the control each needs.
 *
 * One list drives every condition in the editor — a layer's `show_when`, a
 * group's arm selection, and the `when` on a single rule — because they are
 * the same grammar. Widening what an author can branch on is an entry here.
 *
 * `paths` must stay in step with the `condition_leaf` variants in
 * tc.artwork.template.v3 / tc.artwork.hero.v2; a subject the schema does not
 * admit is rejected at publish, not here.
 */
export type ConditionOperator = 'is' | 'is not' | 'is one of'

export interface ConditionSubject {
  value: string
  label: string
  /** Offered only for hero templates — the matchcard schema rejects it,
   * because a base matchcard is not fetched with a phase. */
  heroOnly?: boolean
  /** A boolean fact reads as plain true/false; others compare a value. */
  boolean?: boolean
  /** How a boolean reads after its path's label, held and not held. */
  reads?: { yes: string; no: string }
  /** Which concrete paths this subject can produce, and their labels. */
  paths: { path: string; label: string }[]
  /** Values offered for a non-boolean subject. Empty means free text. */
  values?: readonly string[]
  /** How each value reads, when the raw value would not. */
  valueLabels?: Record<string, string>
}

const TEAMS: Team[] = ['home', 'away']

const teamPaths = (suffix: string, label: (t: string) => string) =>
  TEAMS.map((team) => ({
    path: `${team}.${suffix}`,
    label: label(team === 'home' ? 'Home' : 'Away'),
  }))

const playerPaths = (suffix: string, label: (t: string, i: number) => string) =>
  TEAMS.flatMap((team) =>
    [0, 1].map((index) => ({
      path: `${team}.players[${index}].${suffix}`,
      label: label(team === 'home' ? 'Home' : 'Away', index + 1),
    }))
  )

export const CONDITION_SUBJECTS: ConditionSubject[] = [
  {
    value: 'all_have_headshots',
    label: 'A side has headshots for everyone',
    boolean: true,
    reads: { yes: 'has every headshot', no: 'is missing a headshot' },
    paths: teamPaths('all_have_headshots', (t) => `${t} side`),
  },
  {
    value: 'has_headshot',
    label: 'A player has a headshot',
    boolean: true,
    reads: { yes: 'has a headshot', no: 'has no headshot' },
    paths: playerPaths('has_headshot', (t, i) => `${t} player ${i}`),
  },
  {
    value: 'headshots',
    label: 'Which sides have every headshot',
    paths: [{ path: 'headshots', label: 'Headshots' }],
    values: ['both', 'home', 'away', 'neither'],
    valueLabels: {
      both: 'both sides have every headshot',
      home: 'only the home side has every headshot',
      away: 'only the away side has every headshot',
      neither: 'neither side has every headshot',
    },
  },
  {
    value: 'category',
    label: 'Match category',
    paths: [{ path: 'category', label: 'Match category' }],
  },
  {
    value: 'phase',
    label: 'Match lifecycle phase',
    heroOnly: true,
    paths: [{ path: 'phase', label: 'Match lifecycle phase' }],
    values: HERO_PHASES,
  },
  {
    value: 'tier',
    label: 'Tournament tier',
    paths: [{ path: 'tier', label: 'Tournament tier' }],
    values: TIERS,
    valueLabels: {
      grand_slam: 'the tournament is a Grand Slam',
      finals: 'the tournament is a tour finals',
      '1000': 'the tournament is a 1000',
      '500': 'the tournament is a 500',
      '250': 'the tournament is a 250',
      '125': 'the tournament is a 125',
      challenger: 'the tournament is a Challenger',
      other: 'the tournament is another level',
    },
  },
  {
    value: 'round_stage',
    label: 'Round',
    paths: [{ path: 'round_stage', label: 'Round' }],
    values: ROUND_STAGES,
    valueLabels: {
      final: 'the match is the final',
      semifinal: 'the match is a semifinal',
      quarterfinal: 'the match is a quarterfinal',
      earlier: 'the match is an earlier main-draw round',
      qualifying: 'the match is in qualifying',
    },
  },
  {
    value: 'surface',
    label: 'Surface',
    paths: [{ path: 'surface', label: 'Surface' }],
    values: SURFACES,
  },
  {
    value: 'indoor',
    label: 'Played indoors',
    boolean: true,
    reads: { yes: 'is indoors', no: 'is outdoors' },
    paths: [{ path: 'indoor', label: 'The match' }],
  },
  {
    value: 'format',
    label: 'Format',
    paths: [{ path: 'format', label: 'Format' }],
    values: FORMATS,
    valueLabels: {
      singles: 'the match is singles',
      doubles: 'the match is doubles',
      mixed_doubles: 'the match is mixed doubles',
    },
  },
  {
    value: 'gender',
    label: 'Draw gender',
    paths: [{ path: 'gender', label: 'Draw gender' }],
    values: GENDERS,
  },
  {
    value: 'seeded',
    label: 'A side is seeded',
    boolean: true,
    reads: { yes: 'is seeded', no: 'is unseeded' },
    paths: teamPaths('seeded', (t) => `${t} side`),
  },
  {
    value: 'entry',
    label: 'How a side entered the draw',
    paths: teamPaths('entry', (t) => `${t} side entry`),
    values: ENTRIES,
    valueLabels: {
      wildcard: 'wildcard',
      qualifier: 'qualifier',
      lucky_loser: 'lucky loser',
      alternate: 'alternate',
    },
  },
]

export const subjectForPath = (path: string): ConditionSubject | undefined =>
  CONDITION_SUBJECTS.find((s) => s.paths.some((p) => p.path === path))

/** A leaf as the editor thinks of it: subject, path, operator, values. */
export interface ParsedLeaf {
  subject: ConditionSubject
  path: string
  operator: ConditionOperator
  values: string[]
}

/**
 * Read a condition the editor can present, or null.
 *
 * Only a leaf, or a `not` wrapping a leaf, round-trips through the simple
 * control; `all`/`any` are legitimate and renderable but have no UI yet, so
 * they report as unparsed and the editor says so rather than silently
 * rewriting them.
 */
export function parseCondition(condition: Condition | undefined): ParsedLeaf | null {
  if (!condition) return null
  const negated = isNot(condition)
  const leaf = negated ? condition.not : condition
  if (!isLeaf(leaf)) return null
  const subject = subjectForPath(leaf.path)
  if (!subject) return null

  let operator: ConditionOperator = negated ? 'is not' : 'is'
  let values: string[] = []
  if (Array.isArray(leaf.in)) {
    operator = 'is one of'
    values = leaf.in.map(String)
  } else if (leaf.equals !== undefined) {
    values = [String(leaf.equals)]
  }
  return { subject, path: leaf.path, operator, values }
}

/** Build a condition from what the controls hold. */
export function buildCondition(parsed: ParsedLeaf): Condition {
  const leaf: Condition = { path: parsed.path }
  if (!parsed.subject.boolean) {
    if (parsed.operator === 'is one of') leaf.in = parsed.values
    else if (parsed.values.length) leaf.equals = parsed.values[0]
  }
  return parsed.operator === 'is not' ? { not: leaf } : leaf
}

/** The condition a freshly added control starts from. */
export const defaultCondition = (isHero: boolean): Condition => {
  const subject = CONDITION_SUBJECTS.find((s) => isHero || !s.heroOnly)!
  return { path: subject.paths[0].path }
}

/** A one-line reading of a condition, for the layer tree and rule rows. */
export function describeCondition(condition: Condition | undefined): string {
  if (!condition) return 'always'
  if (isAll(condition))
    return condition.all.map(describeCondition).join(' and ')
  if (isAny(condition))
    return condition.any.map(describeCondition).join(' or ')
  const parsed = parseCondition(condition)
  if (!parsed) return 'a condition this editor cannot read'
  const path =
    parsed.subject.paths.find((p) => p.path === parsed.path)?.label ?? parsed.path
  if (parsed.subject.boolean) {
    const reads = parsed.subject.reads
    const held = parsed.operator !== 'is not'
    if (reads) return `${path} ${held ? reads.yes : reads.no}`
    return held ? path : `not: ${path}`
  }
  const labels = parsed.subject.valueLabels
  if (labels && parsed.values.length) {
    let read = parsed.values.map((v) => labels[v] ?? v).join(' or ')
    // A per-side subject's labels name the value, not the side.
    if (parsed.subject.paths.length > 1) read = `${path} is ${read}`
    return parsed.operator === 'is not' ? `not: ${read}` : read
  }
  return `${path} ${parsed.operator} ${parsed.values.join(', ') || '…'}`
}

// ---------------------------------------------------------------------------
// Layer presentation
// ---------------------------------------------------------------------------

export interface LayerChip {
  color: string
  letter: string
  label: string
}

export const LAYER_CHIPS: Record<LayerType, LayerChip> = {
  image: { color: '#2D6A6D', letter: 'I', label: 'Image' },
  text: { color: '#1D483C', letter: 'T', label: 'Text' },
  shape: { color: '#00875D', letter: 'S', label: 'Shape' },
  group: { color: '#5a6b60', letter: 'G', label: 'Group' },
}

export const LAYER_TYPE_LABELS: Record<LayerType, string> = {
  image: 'Image',
  text: 'Text',
  shape: 'Shape',
  group: 'Group',
}

export const ADDABLE_LAYER_TYPES: LayerType[] = ['text', 'image', 'shape', 'group']

/**
 * What one rule can draw, as one flat list.
 *
 * The three entity axes only ever produce a handful of combinations, and
 * "index" was the schema's word for which player on the team — 0-based,
 * meaningless in singles, and jargon in doubles. Spelling the combinations out
 * ("Away player 2 — country flag") removes the concept. The pinned-image and
 * hold-a-box options sit in the same list, so a rule is chosen the same way
 * whatever it draws.
 */
export type RuleKind = 'fixed' | 'entity' | 'reserve'

export interface RuleChoice {
  value: string
  label: string
  kind: RuleKind | 'match'
  /** The picker heading this choice sits under. */
  group?: string
  entity?: { team: Team; index: number }
  asset?: EntityAsset
  /** A match-level entity ("tournament") and its asset ("logo"). */
  match?: { entity: string; asset: MatchAsset }
}

const ENTITY_ASSET_LABELS: Record<EntityAsset, string> = {
  headshot: 'headshot',
  flag: 'country flag',
  banner: 'banner',
}

/** Art the match itself carries; mirrors MATCH_IMAGE_ASSETS in artwork_paths.py. */
export const MATCH_IMAGE_OPTIONS: { entity: string; asset: MatchAsset; label: string }[] = [
  { entity: 'tournament', asset: 'logo', label: 'Tournament — logo' },
  { entity: 'tournament', asset: 'banner', label: 'Tournament — banner' },
  { entity: 'tournament', asset: 'card', label: 'Tournament — card' },
  { entity: 'level', asset: 'logo', label: 'Level logo (Grand Slam, ATP 1000…)' },
]

const isPlayerEntity = (entity: string | undefined): boolean =>
  /^(home|away)\.players\[\d+\]$/.test(entity ?? '')

/** The stable key for a rule, matching a RuleChoice value. */
export function ruleKey(rule: SourceRule): string {
  if (rule.reserve) return 'reserve'
  if (!rule.entity) return 'fixed'
  if (!isPlayerEntity(rule.entity)) return `match:${rule.entity}:${rule.asset}`
  const { team, index } = parseEntity(rule.entity)
  return `entity:${team}:${index}:${rule.asset}`
}

/**
 * Choices for a rule, given what the template targets. A second player is only
 * offered when the template covers doubles — in a singles render `players[1]`
 * resolves to nothing, so the rule would silently never fit.
 *
 * The rule's current choice is always included, even if targeting no longer
 * offers it, so opening a template cannot quietly change what it draws.
 */
export function ruleChoices(
  targeting: Targeting | undefined,
  current: SourceRule
): RuleChoice[] {
  const playerCount = targetsTwoPlayerTeams(targeting) ? 2 : 1

  const choices: RuleChoice[] = []
  for (const team of TEAMS) {
    for (let index = 0; index < playerCount; index++) {
      for (const asset of ['headshot', 'flag', 'banner'] as EntityAsset[]) {
        choices.push({
          value: `entity:${team}:${index}:${asset}`,
          label:
            `${team === 'home' ? 'Home' : 'Away'}` +
            `${playerCount > 1 ? ` player ${index + 1}` : ''}` +
            ` — ${ENTITY_ASSET_LABELS[asset]}`,
          kind: 'entity',
          group: 'Players',
          entity: { team, index },
          asset,
        })
      }
    }
  }
  for (const option of MATCH_IMAGE_OPTIONS) {
    choices.push({
      value: `match:${option.entity}:${option.asset}`,
      label: option.label,
      kind: 'match',
      group: 'Match',
      match: { entity: option.entity, asset: option.asset },
    })
  }
  choices.push({ value: 'fixed', label: 'A fixed image', kind: 'fixed' })
  choices.push({
    value: 'reserve',
    label: 'Nothing — hold an empty box',
    kind: 'reserve',
  })

  const key = ruleKey(current)
  if (!choices.some((c) => c.value === key) && isPlayerEntity(current.entity)) {
    const { team, index } = parseEntity(current.entity)
    choices.unshift({
      value: key,
      label:
        `${team === 'home' ? 'Home' : 'Away'} player ${index + 1} — ` +
        `${ENTITY_ASSET_LABELS[current.asset as EntityAsset] ?? current.asset}` +
        ' (not in this template\u2019s targeting)',
      kind: 'entity',
      entity: { team, index },
      asset: current.asset as EntityAsset,
    })
  }
  return choices
}

/**
 * The rule a choice produces, keeping what still applies.
 *
 * The condition and the box override survive a change of what the rule draws,
 * because they answer different questions — swapping a headshot for a flag
 * should not silently drop "only for ATP".
 */
export function ruleFromChoice(choice: RuleChoice, previous: SourceRule): SourceRule {
  const kept: SourceRule = {}
  if (previous.when) kept.when = previous.when

  if (choice.kind === 'match' && choice.match) {
    return { ...kept, entity: choice.match.entity, asset: choice.match.asset }
  }
  if (choice.kind === 'entity' && choice.entity && choice.asset) {
    return {
      ...kept,
      entity: entityPath(choice.entity.team, choice.entity.index),
      asset: choice.asset,
    }
  }
  if (choice.kind === 'reserve') {
    return {
      ...kept,
      reserve: previous.reserve ?? { size: [90, 60] },
    }
  }
  return {
    ...kept,
    tc_image_id: previous.tc_image_id ?? null,
    ...(previous.label ? { label: previous.label } : {}),
  }
}

/** A rule with no condition always fits, so it ends the list. Both rule
 *  kinds are read the same way, so one predicate serves both lists. */
export const isBackstop = (rule: { when?: Condition }): boolean => !rule.when

export const ENTITY_ASSET_OPTIONS: { value: EntityAsset; label: string }[] = [
  { value: 'headshot', label: 'Headshot' },
  { value: 'flag', label: 'Flag' },
]

export const BOUND_FIELD_OPTIONS: { value: BoundField; label: string }[] = [
  { value: 'last_name', label: 'Last name' },
  { value: 'first_name', label: 'First name' },
  { value: 'full_name', label: 'Full name' },
  { value: 'country_code', label: 'Country code ("USA")' },
]

// Match-level text bindings; mirrors MATCH_TEXT_PATHS in artwork_paths.py,
// plus the hero-only start date/time. Empty when the match has no such value,
// so a rule bound to one falls through to the next.
export interface MatchBoundOption {
  path: string
  label: string
  group: 'Match' | 'Tournament' | 'Season' | 'Draw' | 'Venue'
  hint?: string
  /** The matchcard schema rejects it: only a hero render re-renders on reschedule. */
  heroOnly?: boolean
}

const START_TIME_HINT =
  'Pre-formatted in US Eastern; the card regenerates when the match is rescheduled.'

export const MATCH_BOUND_OPTIONS: MatchBoundOption[] = [
  { path: 'match.start_date_display', label: 'Start date ("Sunday, Aug 10")', group: 'Match', hint: START_TIME_HINT, heroOnly: true },
  { path: 'match.start_time_display', label: 'Start time ("2:05 PM ET")', group: 'Match', hint: START_TIME_HINT, heroOnly: true },
  { path: 'match.round_display', label: 'Round ("Round 2", "Quarterfinal")', group: 'Match' },
  { path: 'match.round_short_display', label: 'Round, short ("R2", "QF")', group: 'Match' },
  { path: 'home.seed', label: 'Home seed ("3")', group: 'Match' },
  { path: 'away.seed', label: 'Away seed ("3")', group: 'Match' },
  { path: 'home.entry_display', label: 'Home entry ("WC", "Q", "LL")', group: 'Match' },
  { path: 'away.entry_display', label: 'Away entry ("WC", "Q", "LL")', group: 'Match' },
  { path: 'tournament.name', label: 'Tournament name', group: 'Tournament' },
  { path: 'tournament.official_name', label: 'Official title ("Roland Garros")', group: 'Tournament' },
  { path: 'tournament.level', label: 'Level ("Grand Slam", "ATP 1000")', group: 'Tournament' },
  { path: 'season.name', label: 'Season name ("Roland Garros 2026")', group: 'Season' },
  { path: 'season.year', label: 'Year ("2026")', group: 'Season' },
  { path: 'season.surface', label: 'Surface ("Clay")', group: 'Season' },
  { path: 'draw.name', label: 'Draw name ("Men\'s Singles")', group: 'Draw' },
  { path: 'draw.short_name', label: 'Draw short name', group: 'Draw' },
  { path: 'draw.category', label: 'Category ("ATP")', group: 'Draw' },
  { path: 'draw.gender', label: 'Gender ("Men")', group: 'Draw' },
  { path: 'draw.format', label: 'Format ("Mixed Doubles")', group: 'Draw' },
  { path: 'venue.complex_name', label: 'Venue ("Stade Roland Garros")', group: 'Venue' },
]

export const parseMatchBound = (path: string | undefined): MatchBoundOption | null =>
  MATCH_BOUND_OPTIONS.find((o) => o.path === path) ?? null

/**
 * What one text rule can draw, as one flat list — the same treatment
 * ruleChoices gives an image rule, and for the same reason: team, player and
 * field are three axes producing a handful of combinations, and spelling them
 * out ("Away player 2 — last name") removes "index" from the editor.
 */
export type TextRuleKind = 'player' | 'match' | 'static'

export interface TextRuleChoice {
  value: string
  label: string
  kind: TextRuleKind
  /** The picker heading this choice sits under. */
  group?: string
  bound?: { team: Team; index: number; field: BoundField }
  /** A match-level bound path. */
  match?: string
}

/** The stable key for a text rule, matching a TextRuleChoice value. */
export function textRuleKey(rule: TextRule): string {
  if ('static' in rule) return 'static'
  const match = parseMatchBound(rule.bound)
  if (match) return `match:${match.path}`
  const { team, index, field } = parseBound(rule.bound)
  return `player:${team}:${index}:${field}`
}

/**
 * Choices for a text rule, given what the template targets.
 *
 * Same two rules as ruleChoices: a second player only where the template
 * covers doubles, because `players[1]` resolves to nothing in a singles
 * render; and the rule's current choice is always included even when
 * targeting no longer offers it, so opening a template cannot quietly change
 * what it says. Hero-only bindings appear only for a hero template or a rule
 * already using one.
 */
export function textRuleChoices(
  targeting: Targeting | undefined,
  current: TextRule,
  isHero: boolean
): TextRuleChoice[] {
  const playerCount = targetsTwoPlayerTeams(targeting) ? 2 : 1
  const key = textRuleKey(current)

  const choices: TextRuleChoice[] = []
  for (const team of TEAMS) {
    for (let index = 0; index < playerCount; index++) {
      for (const field of BOUND_FIELD_OPTIONS) {
        choices.push({
          value: `player:${team}:${index}:${field.value}`,
          label:
            `${team === 'home' ? 'Home' : 'Away'}` +
            `${playerCount > 1 ? ` player ${index + 1}` : ''}` +
            ` — ${field.label.toLowerCase()}`,
          kind: 'player',
          group: 'Players',
          bound: { team, index, field: field.value },
        })
      }
    }
  }
  for (const option of MATCH_BOUND_OPTIONS) {
    if (option.heroOnly && !isHero && key !== `match:${option.path}`) continue
    choices.push({
      value: `match:${option.path}`,
      label: option.label,
      kind: 'match',
      group: option.group,
      match: option.path,
    })
  }
  choices.push({ value: 'static', label: 'Text you type', kind: 'static' })

  if (!choices.some((c) => c.value === key) && 'bound' in current) {
    const { team, index, field } = parseBound(current.bound)
    choices.unshift({
      value: key,
      label:
        `${team === 'home' ? 'Home' : 'Away'} player ${index + 1} — ` +
        `${field.replace('_', ' ')} (not in this template’s targeting)`,
      kind: 'player',
      bound: { team, index, field },
    })
  }
  return choices
}

/** The rule a choice produces, keeping the condition that reached it. */
export function textRuleFromChoice(
  choice: TextRuleChoice,
  previous: TextRule
): TextRule {
  if (choice.kind === 'player' && choice.bound) {
    const { team, index, field } = choice.bound
    return textRuleWith(previous, { bound: boundPath(team, index, field) })
  }
  if (choice.kind === 'match' && choice.match) {
    return textRuleWith(previous, { bound: choice.match })
  }
  // Typed text survives a trip through a binding and back, so switching to
  // compare what a field resolves to does not cost the author their words.
  return textRuleWith(previous, {
    static: 'static' in previous ? previous.static : '',
  })
}

export const TEAM_OPTIONS: { value: Team; label: string }[] = [
  { value: 'home', label: 'Home' },
  { value: 'away', label: 'Away' },
]

export const TEXT_TRANSFORM_OPTIONS: { value: TextTransform | ''; label: string }[] = [
  { value: '', label: 'As typed' },
  { value: 'uppercase', label: 'UPPERCASE' },
  { value: 'lowercase', label: 'lowercase' },
  { value: 'titlecase', label: 'Titlecase' },
]

/**
 * How an image is sized, as six explicit modes.
 *
 * The stored schema spreads this across three things — `box.fill`, `box.fit`,
 * and whether each `box.size` entry is a number, null, or absent — so "no fit"
 * alone has four different outcomes, including a trap where omitting `size`
 * silently renders the image 60px tall. These modes are the outcomes instead,
 * and each writes exactly the keys the renderer reads for it.
 *
 * CSS equivalents, where there is one: `exact` is object-fit: fill, `cover` is
 * object-fit: cover with object-position: center, `canvas` is object-fit: fill
 * at canvas size. `contain` has no equivalent — nothing in the renderer
 * letterboxes, so an image can't be bounded by both sides without distorting or
 * cropping.
 */
export type SizingMode =
  | 'canvas'
  | 'cover'
  | 'exact'
  | 'fixedWidth'
  | 'fixedHeight'
  | 'canvasWidth'

/** Which width/height inputs a mode actually reads. */
export type SizingFields = 'none' | 'both' | 'width' | 'height'

export const SIZING_MODES: {
  value: SizingMode
  label: string
  help: string
  fields: SizingFields
}[] = [
  {
    value: 'canvas',
    label: 'Whole canvas (background)',
    help:
      'Stretched to exactly the canvas, so proportions are not preserved — use ' +
      'art already cut to this size. Registers no box, so it has no handles and ' +
      'nothing can anchor to it. CSS: object-fit: fill at canvas size.',
    fields: 'none',
  },
  {
    value: 'cover',
    label: 'Cover a box, crop the overflow',
    help:
      'Scales until the image covers the box, then centre-crops the excess. ' +
      'Never distorts. This is how headshots are placed. CSS: object-fit: cover ' +
      'with object-position: center.',
    fields: 'both',
  },
  {
    value: 'exact',
    label: 'Exact size (distorts)',
    help:
      'Resizes to exactly these dimensions, squashing the image if they don’t ' +
      'match its proportions. CSS: object-fit: fill.',
    fields: 'both',
  },
  {
    value: 'fixedWidth',
    label: 'Fixed width, auto height',
    help: 'Scales to this width; the height follows the image’s proportions.',
    fields: 'width',
  },
  {
    value: 'fixedHeight',
    label: 'Fixed height, auto width',
    help:
      'Scales to this height; the width follows the image’s proportions. This is ' +
      'how the flags are placed.',
    fields: 'height',
  },
  {
    value: 'canvasWidth',
    label: 'Full canvas width, auto height',
    help:
      'Scales the image so its width matches the canvas — no dimensions of its ' +
      'own. Equivalent to a fixed width set to the canvas width, so it’s a ' +
      'shorthand that tracks the canvas rather than a distinct behaviour. The ' +
      'seeded plates use it.',
    fields: 'none',
  },
]

const SIZING_FIELDS: Record<SizingMode, SizingFields> = SIZING_MODES.reduce(
  (acc, m) => ({ ...acc, [m.value]: m.fields }),
  {} as Record<SizingMode, SizingFields>
)

export const sizingFieldsFor = (mode: SizingMode): SizingFields => SIZING_FIELDS[mode]

/**
 * The mode a stored box is in, or null when it's a state the modes can't
 * express — no `size` key (renders 60px tall), `[null, null]` (intrinsic size),
 * or the unimplemented `fit: "stretch"`. None of those occur in any seeded body;
 * they're surfaced rather than silently coerced so a hand-edited body isn't
 * quietly rewritten.
 */
export function sizingModeOf(box: ImageBox | undefined): SizingMode | null {
  const b = box ?? {}
  if (b.fill === 'canvas') return 'canvas'
  if (b.fit === 'width') return 'canvasWidth'
  if (b.fit === 'cover') return 'cover'
  if (b.fit) return null // 'stretch' — in the enum, no branch in the renderer
  if (!b.size) return null // defaults to [null, 60] at render time
  const [w, h] = b.size
  if (w !== null && h !== null) return 'exact'
  if (w !== null) return 'fixedWidth'
  if (h !== null) return 'fixedHeight'
  return null // [null, null] — intrinsic size
}

/**
 * The box for `mode`, carrying over dimensions the new mode still reads and
 * dropping the keys it doesn't. Dropping matters: leaving an ignored `size` on a
 * canvas fill would publish a body that says something the renderer never looks
 * at. Switching away and back therefore loses the old numbers — undo covers it.
 */
export function boxForSizingMode(
  box: ImageBox | undefined,
  mode: SizingMode,
  canvas: { width: number; height: number }
): ImageBox {
  const b = box ?? {}
  const [w, h] = b.size ?? [null, null]
  const at = b.at
  const defaultW = w ?? Math.round(canvas.width / 4)
  const defaultH = h ?? 60

  switch (mode) {
    case 'canvas':
      // Position is never read for a canvas fill, so it goes too.
      return { fill: 'canvas' }
    case 'canvasWidth':
      return { ...(at ? { at } : {}), fit: 'width' }
    case 'cover':
      return { ...(at ? { at } : {}), size: [defaultW, h ?? defaultW], fit: 'cover' }
    case 'exact':
      return { ...(at ? { at } : {}), size: [defaultW, h ?? defaultH] }
    case 'fixedWidth':
      return { ...(at ? { at } : {}), size: [defaultW, null] }
    case 'fixedHeight':
      return { ...(at ? { at } : {}), size: [null, defaultH] }
  }
}

// targeting.categories values the seeded templates use. Free text is still
// allowed — this is the picker's shortlist, not a validation set.
export const TARGETING_CATEGORIES = [
  'ATP',
  'WTA',
  'ITF Men',
  'ITF Women',
  'Davis Cup',
  'Billie Jean King Cup',
  'Challenger',
]

// ---------------------------------------------------------------------------
// Conditional image fields
//
// A conditional source picks an image by looking at the match. Today the schema
// allows exactly one field: `when: { category_in: [...] }`, additionalProperties
// false. So there is one entry below and the field dropdown has one option.
//
// It's a table because the engine already has a general condition mechanism it
// isn't using here — condition_holds(when, resolve) handles {path, in: [...]}
// over any path DataContext resolves, which is what group layers use. When the
// conditional source is moved onto it, `category_in` becomes
// {path: 'category', in: [...]} and a new field is an entry here plus a path
// exposed on DataContext. `path` below is already the future wire form; `storage`
// is how it's written until then.
//
// A second entry would read:
//
//   {
//     value: 'surface',
//     label: 'Court surface',
//     path: 'surface',
//     storage: null,            // needs the unified `when` shape
//     suggestions: ['Hard', 'Clay', 'Grass', 'Carpet'],
//   }
//
// (tc_season.tc_surface / v_season.effective_surface already exist; what's
// missing is carrying it onto MatchcardData and exposing it on DataContext.)
// ---------------------------------------------------------------------------

export const MATCH_TYPES: MatchType[] = ['singles', 'doubles', 'mixed_doubles']

/**
 * The match types selection can actually ask for.
 *
 * select_template derives its type as `"doubles" if is_doubles else "singles"`,
 * and is_doubles is already true for a mixed-doubles competition
 * (data_fetcher: `competition_type in ("doubles", "mixed_doubles")`). So
 * 'mixed_doubles' never appears on the asking side: a mixed-doubles match is
 * matched by 'doubles'. It's in the schema enum, and the seeded doubles cards
 * list it alongside 'doubles' where it's redundant — but a template listing only
 * 'mixed_doubles' can never be selected.
 */
const SELECTABLE_MATCH_TYPES: MatchType[] = ['singles', 'doubles']

/** Whether any match could select a template with this targeting. */
export const targetingIsSelectable = (targeting: Targeting | undefined): boolean =>
  (targeting?.types ?? []).some((t) => SELECTABLE_MATCH_TYPES.includes(t))

/** True for a type that's accepted but can never be matched on its own. */
export const matchTypeIsRedundant = (type: MatchType): boolean =>
  !SELECTABLE_MATCH_TYPES.includes(type)

/**
 * Whether a template can render two players per side. Same field the engine
 * selects on, so the editor offers exactly the player slots a match reaching
 * this template could have.
 */
export const targetsTwoPlayerTeams = (targeting: Targeting | undefined): boolean =>
  (targeting?.types ?? []).some((t) => t === 'doubles' || t === 'mixed_doubles')

/**
 * Canvas presets.
 *
 * `selectable` is whether the engine can ever pick a template with this aspect.
 * MatchcardEngine._ASPECT_RATIOS knows only 16:9 and 4:3, and every caller asks
 * for one of those, so a square or portrait template renders correctly on demand
 * (preview, the dev script) but is never chosen for a real card. Marked rather
 * than hidden: the canvas has to exist before anything can request it.
 */
export const CANVAS_PRESETS: {
  value: string
  label: string
  width: number
  height: number
  selectable: boolean
}[] = [
  { value: '1920x1080', label: '16:9 landscape — 1920×1080', width: 1920, height: 1080, selectable: true },
  { value: '1440x1080', label: '4:3 landscape — 1440×1080', width: 1440, height: 1080, selectable: true },
  { value: '1080x1620', label: '2:3 portrait — 1080×1620', width: 1080, height: 1620, selectable: false },
  { value: '1080x1080', label: '1:1 square — 1080×1080', width: 1080, height: 1080, selectable: false },
  { value: '1920x475', label: 'Hero banner — 1920×475', width: 1920, height: 475, selectable: false },
]

// Mirrors MAX_CANVAS_DIMENSION / MAX_CANVAS_PIXELS in matchcard_engine.py. The
// preview rejects anything beyond these, so the editor checks first rather than
// firing a request that's certain to fail on every keystroke.
export const MAX_CANVAS_DIMENSION = 8192
export const MAX_CANVAS_PIXELS = 16_777_216

/** Why a canvas can't be previewed, or null when it's fine. */
export function canvasProblem(canvas: { width: number; height: number }): string | null {
  const { width, height } = canvas
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1) {
    return 'Width and height must both be whole numbers above zero.'
  }
  if (width > MAX_CANVAS_DIMENSION || height > MAX_CANVAS_DIMENSION) {
    return `Each side must be ${MAX_CANVAS_DIMENSION}px or less — a 4K frame is 3840px.`
  }
  if (width * height > MAX_CANVAS_PIXELS) {
    return `${(width * height).toLocaleString()} pixels is too large to render; the limit is ${MAX_CANVAS_PIXELS.toLocaleString()}.`
  }
  return null
}

/** The aspect ratios MatchcardEngine can be asked for, as width/height. */
const SELECTABLE_ASPECTS = [16 / 9, 4 / 3]
const ASPECT_TOLERANCE = 0.01

/**
 * Whether the engine could ever select a template with this canvas. False means
 * the template renders fine in preview but no real card will use it until
 * _ASPECT_RATIOS gains the ratio and a caller requests it.
 */
export const canvasIsSelectable = (canvas: {
  width: number
  height: number
}): boolean => {
  if (!canvas.width || !canvas.height) return false
  const ratio = canvas.width / canvas.height
  return SELECTABLE_ASPECTS.some((r) => Math.abs(ratio - r) <= ASPECT_TOLERANCE)
}

// ---------------------------------------------------------------------------
// Positioning helpers
// ---------------------------------------------------------------------------

/** True when the layer positions relative to another layer. */
export const isAnchored = (layer: Layer): boolean =>
  isPlaced(layer) && Boolean(layer.anchor_to)

/**
 * Where a layer's absolute position lives. The engine reads
 * `box.at` first and falls back to `at` (resolve_position), so an image layer's
 * absolute position must be written to box.at or a box.at of [0,0] would win.
 */
export const absolutePositionField = (layer: PlacedLayer): 'box' | 'at' =>
  layer.type === 'image' ? 'box' : 'at'

export function readPosition(layer: PlacedLayer): Point {
  if (layer.anchor_to) return layer.anchor_to.offset ?? [0, 0]
  if (layer.type === 'image') return layer.box?.at ?? layer.at ?? [0, 0]
  return layer.at ?? [0, 0]
}

/** A patch that moves `layer` to `point`, in whichever field the engine reads. */
export function positionPatch(layer: PlacedLayer, point: Point): Record<string, unknown> {
  if (layer.anchor_to) {
    return { anchor_to: { ...layer.anchor_to, offset: point } }
  }
  if (layer.type === 'image') {
    return { box: { ...(layer.box ?? {}), at: point } }
  }
  return { at: point }
}

// ---------------------------------------------------------------------------
// Normalization + construction
// ---------------------------------------------------------------------------

const DEFAULT_CANVAS = { width: 1920, height: 1080 }

/**
 * Coerce a raw stored body into an editable TemplateBody with the required keys
 * present. Only fills in what the schema requires; it deliberately does not
 * strip unknown keys, because silently dropping a field an author (or a future
 * schema) added would lose work — publish reports those instead.
 */
export function normalizeTemplateBody(raw: unknown, fallbackName = ''): TemplateBody {
  const src = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const canvas = src.canvas as TemplateBody['canvas'] | undefined
  const targeting = (src.targeting ?? {}) as Partial<Targeting>

  return {
    ...(src as object),
    name: typeof src.name === 'string' && src.name ? src.name : fallbackName,
    canvas:
      canvas && typeof canvas.width === 'number' && typeof canvas.height === 'number'
        ? { width: canvas.width, height: canvas.height }
        : { ...DEFAULT_CANVAS },
    targeting: {
      ...targeting,
      types: Array.isArray(targeting.types) && targeting.types.length
        ? targeting.types
        : ['singles'],
      resolution: targeting.resolution ?? '1080',
    },
    layers: Array.isArray(src.layers) ? (src.layers as Layer[]) : [],
  } as TemplateBody
}

/** A body read from an imported file, or why it cannot be used. */
export function parseImportedBody(
  text: string,
  fallbackName: string
): { body: TemplateBody } | { error: string } {
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch {
    return { error: 'That file is not valid JSON.' }
  }
  if (!raw || typeof raw !== 'object' || !Array.isArray((raw as { layers?: unknown }).layers)) {
    return { error: 'That file is not a template body — it has no layers list.' }
  }
  return { body: normalizeTemplateBody(raw, fallbackName) }
}

// The only top-level keys tc.artwork.template.v2 allows. Anything else fails
// publish, so the editor has to be able to name and drop them.
const KNOWN_TOP_LEVEL_KEYS = ['name', 'canvas', 'targeting', 'layers']

/**
 * Top-level keys the schema will reject. A v1 body opened in this editor still
 * carries `selector` / `dimensions` / `output_format`; normalization keeps them
 * rather than silently discarding an author's data, so the editor offers to drop
 * them instead — otherwise publish would be blocked with no way to fix it here.
 */
export const unknownTopLevelKeys = (body: TemplateBody | null): string[] =>
  body ? Object.keys(body).filter((k) => !KNOWN_TOP_LEVEL_KEYS.includes(k)) : []

/** Ascending z = back to front. Ties keep declaration order (stable sort). */
export const byZ = <T extends { layer: Layer }>(items: T[]): T[] =>
  [...items].sort((a, b) => zOf(a.layer) - zOf(b.layer))

export const zOf = (layer: Layer): number =>
  isPlaced(layer) ? (layer.z ?? 0) : 0

/**
 * The z a layer occupies for *presentation* ordering in the layer tree.
 *
 * A group carries no z of its own — the schema forbids one — but its contents
 * do, and the engine sorts every layer globally by z once groups are expanded.
 * So a group's place in the stack is where its contents start: the lowest z
 * among its descendants. Scoring a group as 0 instead files it behind the
 * background, which is how the doubles card came to list background first while
 * singles listed it last.
 *
 * A group spanning several z values (the doubles arms run 10 to 30) can't
 * occupy one slot faithfully; the lowest is where it enters the stack.
 */
export function layerStackZ(layer: Layer): number {
  if (isPlaced(layer)) return layer.z ?? 0
  const inner = descendantZs(layer)
  return inner.length ? Math.min(...inner) : 0
}

const descendantZs = (group: GroupLayer): number[] =>
  flattenLayers(group.layers ?? [])
    .filter(({ layer }) => isPlaced(layer))
    .map(({ layer }) => (layer as PlacedLayer).z ?? 0)


const nextZ = (layers: Layer[]): number =>
  flattenLayers(layers).reduce((max, { layer }) => Math.max(max, zOf(layer)), 0) + 10

const uniqueId = (base: string, layers: Layer[]): string => {
  const existing = new Set(
    flattenLayers(layers)
      .map(({ layer }) => (isPlaced(layer) ? layer.id : null))
      .filter((id): id is string => Boolean(id))
  )
  if (!existing.has(base)) return base
  let n = 2
  while (existing.has(`${base}_${n}`)) n++
  return `${base}_${n}`
}

/** Build a new layer of `type` with defaults that render something visible. */
export function makeLayer(
  type: LayerType,
  layers: Layer[],
  canvas: { width: number; height: number },
  isHero = false
): Layer {
  const z = nextZ(layers)
  const cx = Math.round(canvas.width / 2)
  const cy = Math.round(canvas.height / 2)

  switch (type) {
    case 'group':
      // Starts empty; the tree offers an add button inside it.
      return {
        type: 'group',
        show_when: defaultCondition(isHero),
        layers: [],
      }
    case 'image':
      return {
        id: uniqueId('image', layers),
        name: 'Image',
        type: 'image',
        z,
        box: { at: [cx - 200, cy - 200], size: [400, null] },
        // One rule, no condition: the backstop. A list always has one, so a
        // new layer cannot start in the state where nothing ever draws.
        sources: [{ tc_image_id: null }],
      }
    case 'shape':
      return {
        id: uniqueId('shape', layers),
        name: 'Shape',
        type: 'shape',
        z,
        size: [600, 120],
        color: '#000000',
        opacity: 255,
        at: [cx - 300, cy - 60],
      }
    case 'text':
    default:
      return {
        id: uniqueId('text', layers),
        name: 'Text',
        type: 'text',
        z,
        // One rule, no condition: the backstop, exactly as a new image layer
        // starts. A list always has one, so a new layer cannot start in the
        // state where nothing ever draws.
        content: [{ bound: boundPath('home', 0, 'last_name') }],
        font: { tc_font_id: null, size: 96 },
        max_width: 700,
        color: '#FFFFFF',
        transform: 'uppercase',
        at: [cx - 350, cy - 48],
      }
  }
}

/** What a placed layer is called: its name, else its (generated) id. */
export const layerName = (layer: PlacedLayer): string => layer.name || layer.id

/** A human label for a layer in the tree. */
export function layerLabel(layer: Layer): string {
  if (isGroup(layer)) {
    if (layer.name) return layer.name
    return layer.show_when ? `when ${describeCondition(layer.show_when)}` : 'group'
  }
  return layerName(layer)
}

/** Names of the layers an anchor can reference, keyed by id. */
export const layerNamesById = (layers: Layer[]): Map<string, string> =>
  new Map(
    flattenLayers(layers)
      .map(({ layer }) => layer)
      .filter(isPlaced)
      .map((layer) => [layer.id, layerName(layer)])
  )

/** Whether the tree should mark this layer as conditional.
 *
 * A group states its condition in its label. On any other layer
 * a condition is otherwise invisible — the layer simply does not appear in a
 * render, with nothing to explain why — so the tree needs a mark. */
export const isConditional = (layer: Layer): boolean =>
  !isGroup(layer) && Boolean(layer.show_when)

/** A one-line summary of what a layer will show, for the tree's second line. */
export function layerSubtitle(layer: Layer): string {
  if (isText(layer)) {
    const rules = layer.content ?? []
    if (rules.length > 1) return `${rules.length} rules`
    return rules[0] ? describeTextRule(rules[0]) : 'no rules'
  }
  if (isImage(layer)) {
    const rules = layer.sources ?? []
    if (rules.length > 1) return `${rules.length} rules`
    const only = rules[0]
    if (!only) return 'no rules'
    if (only.reserve) return 'holds an empty box'
    if (only.entity) return `${only.entity} · ${only.asset}`
    return only.label ?? (only.tc_image_id ? 'fixed image' : 'no image')
  }
  if (isShape(layer)) {
    return `${layer.size?.[0] ?? 0}×${layer.size?.[1] ?? 0}`
  }
  if (isGroup(layer)) {
    const n = layer.layers?.length ?? 0
    const count = `${n} layer${n === 1 ? '' : 's'}`
    // A named group still shows its condition, one line down.
    return layer.name && layer.show_when
      ? `when ${describeCondition(layer.show_when)} · ${count}`
      : count
  }
  return ''
}
